import { apiClient } from './Api';
import { VOUCHER_ROUTE_API } from '../constants';
import { voucherToUpdatePayload, fetchUsage } from './voucherBulkManage';

// Fields the group edit can change. `code` is intentionally absent: editing a
// live code breaks anything already distributed. voucher_type is allowed but
// flagged, since changing it changes how the voucher is reached.
export const EDITABLE_FIELDS = [
  { key: 'voucher_type', label: 'Voucher type', kind: 'select', options: ['public', 'private', 'public_open', 'in_store'], flag: 'Changes how the voucher is reached (assignment vs code vs auto-listed).' },
  { key: 'type', label: 'Discount type', kind: 'select', options: ['percentage', 'fixed'] },
  { key: 'amount', label: 'Discount value', kind: 'int' },
  { key: 'max_amount', label: 'Max discount (Rp)', kind: 'intOrNull' },
  { key: 'minimum_purchase', label: 'Minimum purchase (Rp)', kind: 'int' },
  { key: 'started_at', label: 'Valid from (YYYY-MM-DD HH:mm)', kind: 'datetime' },
  { key: 'ended_at', label: 'Valid until (YYYY-MM-DD HH:mm)', kind: 'datetime' },
  { key: 'limit', label: 'Total limit', kind: 'intOrNull' },
  { key: 'limit_per_user', label: 'Limit per user', kind: 'intOrNull' },
  { key: 'platform_specifications', label: 'Only use for', kind: 'select', options: ['', 'APP', 'WEB'], optionLabels: { '': 'All', APP: 'App', WEB: 'Web' } },
  { key: 'active', label: 'Status', kind: 'select', options: ['1', '0'], optionLabels: { 1: 'Active', 0: 'Inactive' } },
  { key: 'auto_apply', label: 'Auto apply', kind: 'bool' },
];

function coerce(key, raw) {
  const def = EDITABLE_FIELDS.find((f) => f.key === key);
  if (!def) return raw;
  switch (def.kind) {
    case 'int':
      return raw === '' || raw === null ? (key === 'minimum_purchase' ? 0 : null) : parseInt(raw, 10);
    case 'intOrNull': {
      if (raw === '' || raw === null) return null;
      const n = parseInt(raw, 10);
      return key === 'max_amount' && n === 0 ? null : n;
    }
    case 'bool':
      return raw === true || raw === '1' || raw === 1;
    case 'datetime':
      if (raw === '' || raw === null) return null;
      return /\d{2}:\d{2}/.test(raw) ? raw.replace(/(\d{2}:\d{2})(:\d{2})?$/, '$1:00') : raw;
    case 'select':
      if (key === 'platform_specifications') return raw ? raw : null;
      if (key === 'active') return raw === '1' || raw === 1;
      return raw;
    default:
      return raw;
  }
}

function displayOld(key, v) {
  if (key === 'active') return v.active ? 'active' : 'inactive';
  if (key === 'platform_specifications') return v.platform_specifications || 'All';
  const val = v[key];
  return val === null || val === undefined || val === '' ? '-' : String(val);
}

function displayNew(key, raw) {
  if (key === 'active') return raw === '1' || raw === 1 ? 'active' : 'inactive';
  if (key === 'platform_specifications') return raw ? raw : 'All';
  if (key === 'auto_apply') return raw === true || raw === '1' ? 'yes' : 'no';
  return raw === '' || raw === null ? '-' : String(raw);
}

// overrides: { fieldKey: rawValue } for the fields the operator chose to change.
export async function buildEditPreview(vouchers, overrides, onProgress) {
  const keys = Object.keys(overrides);
  const rows = [];
  for (let i = 0; i < vouchers.length; i += 1) {
    const v = vouchers[i];
    const row = { id: v.id, code: v.code, name: v.name, skipped: false, skipReason: '', changes: [] };
    // eslint-disable-next-line no-await-in-loop
    const u = await fetchUsage(v.id);
    if (u.error) { row.skipped = true; row.skipReason = 'usage check failed'; }
    else if (u.transaction_usages > 0) { row.skipped = true; row.skipReason = `used in ${u.transaction_usages} transaction(s)`; }
    if (!row.skipped) {
      keys.forEach((k) => {
        const oldD = displayOld(k, v);
        const newD = displayNew(k, overrides[k]);
        if (oldD !== newD) row.changes.push({ field: k, old: oldD, next: newD });
      });
      if (!row.changes.length) { row.skipped = true; row.skipReason = 'no change'; }
    }
    rows.push(row);
    if (onProgress) onProgress(i + 1, vouchers.length);
  }
  return rows;
}

export async function runEdit(vouchers, previewRows, overrides, onProgress) {
  const byId = {};
  vouchers.forEach((v) => { byId[v.id] = v; });
  const targets = previewRows.filter((r) => !r.skipped);
  const keys = Object.keys(overrides);
  const results = [];
  for (let i = 0; i < targets.length; i += 1) {
    const r = targets[i];
    const v = byId[r.id];
    const result = { id: r.id, code: r.code, status: 'ok' };
    try {
      const base = voucherToUpdatePayload(v, v.active ? true : false);
      keys.forEach((k) => { base[k] = coerce(k, overrides[k]); });
      // eslint-disable-next-line no-await-in-loop
      await apiClient().put(`${VOUCHER_ROUTE_API}/${r.id}`, base);
    } catch (err) {
      result.status = 'failed';
      const data = err && err.response && err.response.data;
      result.error = (data && (data.message || (data.error && data.error.message))) || (err && err.message) || 'request failed';
    }
    results.push(result);
    if (onProgress) onProgress(i + 1, targets.length, result);
    if (result.status === 'failed') break;
  }
  return results;
}
